import { denormalizeTransactions } from './denormalizeTransactions';

/**
 * Builds all financial visualisation datasets from a loaded customer profile.
 * Returns income trends, CC spend breakdowns, balance history, and insights
 * ready to hand directly to chart components.
 *
 * @param {object} profile - profile.accounts + profile.customer
 * @param {string} source  - profile source label (e.g. 'ahmed-small')
 * @returns {object|null}
 */
export function computeVizFromProfile(profile, source) {
  if (!profile || !profile.accounts) return null;
  const { accounts, customer } = profile;

  const chqTxns = denormalizeTransactions(accounts.chequing?.transactions);
  const ccTxns  = denormalizeTransactions(accounts.credit_card?.transactions);
  const savTxns = denormalizeTransactions(accounts.savings?.transactions);

  // Derive the last 6 months present in the data
  const allDates  = [...chqTxns, ...ccTxns, ...savTxns].map(t => t.date?.slice(0, 7)).filter(Boolean);
  const MONTH_KEYS = [...new Set(allDates)].sort().slice(-6);
  if (MONTH_KEYS.length === 0) return null;
  const MONTH_LABELS = MONTH_KEYS.map(k =>
    ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][parseInt(k.split('-')[1], 10) - 1]
  );

  // 1. Income by month — auto-detect income categories
  const positiveCats  = [...new Set(chqTxns.filter(t => t.amount > 0).map(t => t.category).filter(Boolean))];
  const INCOME_KW     = ['income','salary','payroll','employment','gig','freelance','deposit'];
  let incomeCatKeys   = positiveCats.filter(c => INCOME_KW.some(k => c.toLowerCase().includes(k)));
  if (incomeCatKeys.length === 0) incomeCatKeys = positiveCats.slice(0, 4);
  if (incomeCatKeys.length === 0) incomeCatKeys = ['Income'];

  const incomeData = MONTH_KEYS.map((key, i) => {
    const t     = chqTxns.filter(t => t.date?.startsWith(key) && t.amount > 0);
    const entry = { month: MONTH_LABELS[i] };
    if (incomeCatKeys[0] === 'Income') {
      entry['Income'] = Math.round(t.reduce((s, tx) => s + tx.amount, 0));
    } else {
      incomeCatKeys.forEach(cat => {
        entry[cat] = Math.round(t.filter(tx => tx.category === cat).reduce((s, tx) => s + tx.amount, 0));
      });
    }
    return entry;
  });

  // 2. CC spend donut by category
  const purchases  = ccTxns.filter(t => t.amount < 0);
  const catMap     = {};
  purchases.forEach(t => { const c = t.category || 'Other'; catMap[c] = (catMap[c] || 0) + Math.abs(t.amount); });
  const sorted     = Object.entries(catMap).sort((a, b) => b[1] - a[1]);
  const TOP_N      = 7;
  const topEntries = sorted.slice(0, TOP_N);
  const otherTotal = sorted.slice(TOP_N).reduce((s, [, v]) => s + v, 0);
  const topCatNames = topEntries.map(([n]) => n);
  const donutData  = topEntries.map(([name, value]) => ({ name, value: Math.round(value) }));
  if (otherTotal > 0) donutData.push({ name: 'Other', value: Math.round(otherTotal) });
  const totalCCSpend = Math.round(Object.values(catMap).reduce((s, v) => s + v, 0));

  // 3. Monthly CC spend stacked by top categories
  const allCCCats    = otherTotal > 0 ? [...topCatNames, 'Other'] : topCatNames;
  const ccMonthlyData = MONTH_KEYS.map((key, i) => {
    const mp    = purchases.filter(t => t.date?.startsWith(key));
    const entry = { month: MONTH_LABELS[i] };
    topCatNames.forEach(cat => {
      entry[cat] = Math.round(mp.filter(t => t.category === cat).reduce((s, t) => s + Math.abs(t.amount), 0));
    });
    if (otherTotal > 0) {
      entry['Other'] = Math.round(mp.filter(t => !topCatNames.includes(t.category)).reduce((s, t) => s + Math.abs(t.amount), 0));
    }
    return entry;
  });

  // 4. Month-end chequing balance
  const balanceData = MONTH_KEYS.map((key, i) => {
    const last = [...chqTxns.filter(t => t.date?.startsWith(key))]
      .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
      .pop();
    return { month: MONTH_LABELS[i], Balance: last ? Math.round(last.balance_after) : null };
  });

  // 5. Top chequing debit categories (monthly average)
  const debitMap = {};
  chqTxns.filter(t => t.amount < 0).forEach(t => {
    const c = t.category || 'Other';
    debitMap[c] = (debitMap[c] || 0) + Math.abs(t.amount);
  });
  const numMonths = MONTH_KEYS.length || 1;
  const fixedData = Object.entries(debitMap)
    .map(([name, total]) => ({ name, value: Math.round(total / numMonths) }))
    .filter(d => d.value > 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, 8);

  // Summary stats
  const totalIncome     = incomeData.reduce((s, m) => s + incomeCatKeys.reduce((ss, k) => ss + (m[k] || 0), 0), 0);
  const totalSaved      = savTxns.filter(t => t.amount > 0).reduce((s, t) => s + t.amount, 0);
  const cashBack        = Math.round(ccTxns.reduce((s, t) => s + (t.rewards_earned || 0), 0));
  const avgMonthlyIncome = Math.round(totalIncome / numMonths);
  const avgMonthlyCC    = Math.round(totalCCSpend / numMonths);

  // Period label
  const periodLabel = MONTH_KEYS.length >= 2
    ? `${MONTH_LABELS[0]} ${MONTH_KEYS[0].slice(0, 4)} — ${MONTH_LABELS[MONTH_LABELS.length - 1]} ${MONTH_KEYS[MONTH_KEYS.length - 1].slice(0, 4)}`
    : MONTH_LABELS[0] || '';
  const txnCount  = chqTxns.length + ccTxns.length + savTxns.length;
  const acctCount = [accounts.chequing, accounts.credit_card, accounts.savings, accounts.investments].filter(Boolean).length;

  // Dynamic insights
  const insights = [];
  if (sorted[0]) {
    const [cat, total] = sorted[0];
    insights.push({ icon: '💳', title: `Top Spend: ${cat}`, body: `$${Math.round(total).toLocaleString()} over ${numMonths} months ($${Math.round(total / numMonths).toLocaleString()}/mo avg).` });
  }
  insights.push({ icon: '📊', title: 'Income vs. Spending', body: `Avg monthly income $${avgMonthlyIncome.toLocaleString()} vs. avg CC spend $${avgMonthlyCC.toLocaleString()}.` });
  const validBalances = balanceData.filter(d => d.Balance !== null);
  if (validBalances.length >= 2) {
    const trend = validBalances[validBalances.length - 1].Balance - validBalances[0].Balance;
    insights.push({ icon: trend >= 0 ? '📈' : '📉', title: 'Balance Trend', body: `Chequing balance ${trend >= 0 ? 'up' : 'down'} $${Math.abs(trend).toLocaleString()} over the period.` });
  }

  return {
    customer: customer || {},
    incomeData, donutData, ccMonthlyData, balanceData, fixedData,
    allCCCats, incomeCatKeys, periodLabel, txnCount, acctCount,
    stats: { avgMonthlyIncome, totalSaved: Math.round(totalSaved), totalCCSpend, cashBack },
    insights,
  };
}
